import "./SuperSearchModal.scss";
import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { globalSearch } from "../../../../api/search/globalSearch";
import useAuthStore from "../../../../store/authStore";
import Spinner from "../../../Spinner/Spinner";
import EmptyStatePlaceholder from "../../../EmptyStatePlaceholder/EmptyStatePlaceholder";
import { getTextPreview } from "../../../../utils/textPreview";
import { normalizeColor } from "../../../../utils/colors";
import { useTranslation } from "react-i18next";
import { translateLabel } from "../../../../i18n/defaultLabels";

function SuperSearchModal({ onClose }) {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const [results, setResults] = useState({
    tasks: [],
    notes: [],
    sections: [],
    noteGroups: [],
    lists: [],
    boards: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const selectedItemRef = useRef(null);
  const navigate = useNavigate();
  const { user, token, setCurrentProject, fetchProjects } = useAuthStore();

  // Helper to get color hex - handles both hex codes and color names
  const getColorHex = (color) => {
    if (!color) return "inherit";
    return normalizeColor(color);
  };

  // Auto-focus search input when modal opens
  useEffect(() => {
    const timer = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [selectedIndex]);

  // Debounced search function
  const performSearch = useCallback(
    async (query) => {
      if (!query || query.trim().length === 0) {
        setResults({
          tasks: [],
          notes: [],
          sections: [],
          noteGroups: [],
          lists: [],
          boards: [],
        });
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const data = await globalSearch(query, user?.id || user?._id, 50);
        setResults(data);
      } catch (error) {
        console.error("Search error:", error);
        setResults({
          tasks: [],
          notes: [],
          sections: [],
          noteGroups: [],
          lists: [],
          boards: [],
        });
      } finally {
        setIsLoading(false);
      }
    },
    [user],
  );

  // Handle search input change with debounce
  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    if (searchText) {
      setIsLoading(true);
      debounceTimerRef.current = setTimeout(() => {
        performSearch(searchText);
      }, 300); // 300ms debounce
    } else {
      setResults({
        tasks: [],
        notes: [],
        sections: [],
        noteGroups: [],
        lists: [],
        boards: [],
      });
      setIsLoading(false);
    }

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchText, performSearch]);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchText("");
    setResults({
      tasks: [],
      notes: [],
      sections: [],
      noteGroups: [],
      lists: [],
      boards: [],
    });
  };

  const handleTaskClick = useCallback(
    async (task) => {
      try {
        setIsNavigating(true);

        // Switch to the task's organization if different
        if (
          task.organization?.id &&
          task.organization.id.toString() !== user?.currentOrganization
        ) {
          const response = await fetch(
            `${import.meta.env.VITE_API_URL}/api/auth/switch-organization`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                organizationId: task.organization.id.toString(),
              }),
            },
          );

          if (!response.ok) {
            throw new Error("Failed to switch organization");
          }

          const data = await response.json();
          const { user: updatedUser, currentProject } = data;

          // Update the store with new user and currentProject
          useAuthStore.setState({
            user: updatedUser,
            currentProject: currentProject,
          });

          // Fetch projects for the new organization and wait for it to complete
          await fetchProjects();
        }

        // Set the specific project if different - need to wait for projects to be loaded first
        if (task.project?.id) {
          // Fetch the full project object from the store's projects array
          const projects = useAuthStore.getState().projects;
          const fullProject = projects.find((p) => p._id === task.project.id);
          if (fullProject) {
            setCurrentProject(fullProject);
          }
        }

        // Navigate to boards page with search query and selected task ID
        let url = `/boards?search=${encodeURIComponent(searchText)}&selectTask=${task.id}`;
        if (task.board?.id) {
          url += `&boardId=${task.board.id}`;
        }
        navigate(url);

        // Wait a bit for navigation to start
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Close modal
        onClose();
      } catch (error) {
        console.error("Error switching to task context:", error);
        setIsNavigating(false);
      }
    },
    [
      user,
      token,
      fetchProjects,
      setCurrentProject,
      navigate,
      searchText,
      onClose,
    ],
  );

  const handleNoteClick = useCallback(
    async (note) => {
      try {
        setIsNavigating(true);

        // Switch to the note's organization if different
        if (
          note.organization?.id &&
          note.organization.id.toString() !== user?.currentOrganization
        ) {
          const response = await fetch(
            `${import.meta.env.VITE_API_URL}/api/auth/switch-organization`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                organizationId: note.organization.id.toString(),
              }),
            },
          );

          if (!response.ok) {
            throw new Error("Failed to switch organization");
          }

          const data = await response.json();
          const { user: updatedUser, currentProject } = data;

          // Update the store with new user and currentProject
          useAuthStore.setState({
            user: updatedUser,
            currentProject: currentProject,
          });

          // Fetch projects for the new organization and wait for it to complete
          await fetchProjects();
        }

        // Set the specific project if different - need to wait for projects to be loaded first
        if (note.project?.id) {
          // Fetch the full project object from the store's projects array
          const projects = useAuthStore.getState().projects;
          const fullProject = projects.find((p) => p._id === note.project.id);
          if (fullProject) {
            setCurrentProject(fullProject);
          }
        }

        // Navigate to notes page with search query and selected note ID
        navigate(
          `/notes?search=${encodeURIComponent(searchText)}&selectNote=${note.id}`,
        );

        // Wait a bit for navigation to start
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Close modal
        onClose();
      } catch (error) {
        console.error("Error switching to note context:", error);
        setIsNavigating(false);
      }
    },
    [
      user,
      token,
      fetchProjects,
      setCurrentProject,
      navigate,
      searchText,
      onClose,
    ],
  );

  const handleSectionClick = useCallback(
    async (section) => {
      try {
        setIsNavigating(true);

        // Switch to the section's organization if different
        if (
          section.organization?.id &&
          section.organization.id.toString() !== user?.currentOrganization
        ) {
          const response = await fetch(
            `${import.meta.env.VITE_API_URL}/api/auth/switch-organization`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                organizationId: section.organization.id.toString(),
              }),
            },
          );

          if (!response.ok) {
            throw new Error("Failed to switch organization");
          }

          const data = await response.json();
          const { user: updatedUser, currentProject } = data;

          // Update the store with new user and currentProject
          useAuthStore.setState({
            user: updatedUser,
            currentProject: currentProject,
          });

          // Fetch projects for the new organization and wait for it to complete
          await fetchProjects();
        }

        // Set the specific project if different
        if (section.project?.id) {
          const projects = useAuthStore.getState().projects;
          const fullProject = projects.find(
            (p) => p._id === section.project.id,
          );
          if (fullProject) {
            setCurrentProject(fullProject);
          }
        }

        // Navigate to boards page with search query and board ID
        let url = `/boards?search=${encodeURIComponent(searchText)}`;
        if (section.board?.id) {
          url += `&boardId=${section.board.id}`;
        }
        navigate(url);

        // Wait a bit for navigation to start
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Close modal
        onClose();
      } catch (error) {
        console.error("Error switching to section context:", error);
        setIsNavigating(false);
      }
    },
    [user, token, fetchProjects, setCurrentProject, navigate, onClose],
  );

  const handleBoardClick = useCallback(
    async (board) => {
      try {
        setIsNavigating(true);

        // Switch to the board's organization if different
        if (
          board.organization?.id &&
          board.organization.id.toString() !== user?.currentOrganization
        ) {
          const response = await fetch(
            `${import.meta.env.VITE_API_URL}/api/auth/switch-organization`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                organizationId: board.organization.id.toString(),
              }),
            },
          );

          if (!response.ok) {
            throw new Error("Failed to switch organization");
          }

          const data = await response.json();
          const { user: updatedUser, currentProject } = data;

          // Update the store with new user and currentProject
          useAuthStore.setState({
            user: updatedUser,
            currentProject: currentProject,
          });

          // Fetch projects for the new organization and wait for it to complete
          await fetchProjects();
        }

        // Set the specific project if different
        if (board.project?.id) {
          const projects = useAuthStore.getState().projects;
          const fullProject = projects.find((p) => p._id === board.project.id);
          if (fullProject) {
            setCurrentProject(fullProject);
          }
        }

        // Navigate to boards page with search query and boardId
        navigate(
          `/boards?search=${encodeURIComponent(searchText)}&boardId=${board.id}`,
        );

        // Wait a bit for navigation to start
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Close modal
        onClose();
      } catch (error) {
        console.error("Error switching to board context:", error);
        setIsNavigating(false);
      }
    },
    [user, token, fetchProjects, setCurrentProject, navigate, onClose],
  );

  const handleNoteGroupClick = useCallback(
    async (noteGroup) => {
      try {
        setIsNavigating(true);

        // Switch to the noteGroup's organization if different
        if (
          noteGroup.organization?.id &&
          noteGroup.organization.id.toString() !== user?.currentOrganization
        ) {
          const response = await fetch(
            `${import.meta.env.VITE_API_URL}/api/auth/switch-organization`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                organizationId: noteGroup.organization.id.toString(),
              }),
            },
          );

          if (!response.ok) {
            throw new Error("Failed to switch organization");
          }

          const data = await response.json();
          const { user: updatedUser, currentProject } = data;

          // Update the store with new user and currentProject
          useAuthStore.setState({
            user: updatedUser,
            currentProject: currentProject,
          });

          // Fetch projects for the new organization and wait for it to complete
          await fetchProjects();
        }

        // Set the specific project if different
        if (noteGroup.project?.id) {
          const projects = useAuthStore.getState().projects;
          const fullProject = projects.find(
            (p) => p._id === noteGroup.project.id,
          );
          if (fullProject) {
            setCurrentProject(fullProject);
          }
        }

        // Navigate to notes page with search query
        navigate(`/notes?search=${encodeURIComponent(searchText)}`);

        // Wait a bit for navigation to start
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Close modal
        onClose();
      } catch (error) {
        console.error("Error switching to note group context:", error);
        setIsNavigating(false);
      }
    },
    [user, token, fetchProjects, setCurrentProject, navigate, onClose],
  );

  const handleListClick = useCallback(
    async (list) => {
      try {
        setIsNavigating(true);

        // For project lists, switch to the list's organization if different
        if (
          list.scope === "project" &&
          list.organization?.id &&
          list.organization.id.toString() !== user?.currentOrganization
        ) {
          const response = await fetch(
            `${import.meta.env.VITE_API_URL}/api/auth/switch-organization`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                organizationId: list.organization.id.toString(),
              }),
            },
          );

          if (!response.ok) {
            throw new Error("Failed to switch organization");
          }

          const data = await response.json();
          const { user: updatedUser, currentProject } = data;

          // Update the store with new user and currentProject
          useAuthStore.setState({
            user: updatedUser,
            currentProject: currentProject,
          });

          // Fetch projects for the new organization and wait for it to complete
          await fetchProjects();
        }

        // Set the specific project if it's a project list
        if (list.scope === "project" && list.project?.id) {
          const projects = useAuthStore.getState().projects;
          const fullProject = projects.find((p) => p._id === list.project.id);
          if (fullProject) {
            setCurrentProject(fullProject);
          }
        }

        // Navigate to the correct lists page based on scope with search query
        const listPath =
          list.scope === "project" ? "/project-lists" : "/personal-lists";
        navigate(
          `${listPath}?search=${encodeURIComponent(searchText)}&selectList=${list.id}`,
        );

        // Wait a bit for navigation to start
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Close modal
        onClose();
      } catch (error) {
        console.error("Error switching to list context:", error);
        setIsNavigating(false);
      }
    },
    [user, token, fetchProjects, setCurrentProject, navigate, onClose],
  );

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      const totalResults =
        results.tasks.length +
        results.notes.length +
        results.sections.length +
        results.noteGroups.length +
        (results.lists?.length || 0) +
        (results.boards?.length || 0);
      if (totalResults === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % totalResults);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + totalResults) % totalResults);
      } else if (e.key === "Enter" && selectedIndex >= 0) {
        e.preventDefault();
        // Determine which item is selected and click it
        let currentIndex = 0;

        // Check if it's a task
        if (selectedIndex < results.tasks.length) {
          handleTaskClick(results.tasks[selectedIndex]);
          return;
        }
        currentIndex += results.tasks.length;

        // Check if it's a section
        if (selectedIndex < currentIndex + results.sections.length) {
          const sectionIndex = selectedIndex - currentIndex;
          handleSectionClick(results.sections[sectionIndex]);
          return;
        }
        currentIndex += results.sections.length;

        // Check if it's a note
        if (selectedIndex < currentIndex + results.notes.length) {
          const noteIndex = selectedIndex - currentIndex;
          handleNoteClick(results.notes[noteIndex]);
          return;
        }
        currentIndex += results.notes.length;

        // Check if it's a note group
        if (selectedIndex < currentIndex + results.noteGroups.length) {
          const noteGroupIndex = selectedIndex - currentIndex;
          handleNoteGroupClick(results.noteGroups[noteGroupIndex]);
          return;
        }
        currentIndex += results.noteGroups.length;

        // Check if it's a list
        if (selectedIndex < currentIndex + (results.lists?.length || 0)) {
          const listIndex = selectedIndex - currentIndex;
          handleListClick(results.lists[listIndex]);
          return;
        }
        currentIndex += results.lists?.length || 0;

        // Otherwise it's a board
        const boardIndex = selectedIndex - currentIndex;
        handleBoardClick(results.boards[boardIndex]);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    results,
    selectedIndex,
    handleTaskClick,
    handleNoteClick,
    handleSectionClick,
    handleNoteGroupClick,
    handleListClick,
    handleBoardClick,
  ]);

  const totalResults =
    results.tasks.length +
    results.notes.length +
    results.sections.length +
    results.noteGroups.length +
    (results.lists?.length || 0) +
    (results.boards?.length || 0);

  return (
    <div className="super-search-modal-content">
      {isNavigating && (
        <div className="navigation-overlay">
          <Spinner />
        </div>
      )}

      <div className="title">
        <span>{t("topBar.superSearch")}</span>
        <button className="close-button" onClick={onClose}>
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      <div className="main-wrapper">
        <div className="search-container">
          <input
            ref={searchInputRef}
            type="text"
            className="search-input"
            placeholder={t("search.placeholder")}
            value={searchText}
            onChange={handleSearchChange}
          />
          <span
            className="material-symbols-outlined search-icon"
            style={searchText ? { cursor: "pointer" } : {}}
            onClick={searchText ? handleClearSearch : undefined}
          >
            {searchText ? "close" : "search"}
          </span>
        </div>

        <div className="search-results">
          {isLoading ? (
            <div className="loading-container">
              <Spinner />
              <span className="loading-text">{t("search.searching")}</span>
            </div>
          ) : !searchText ? (
            <div className="empty-state-container">
              <EmptyStatePlaceholder topText={`... ${t("search.emptyHint")}`} />
            </div>
          ) : searchText && totalResults === 0 ? (
            <div className="no-results">
              <span className="material-symbols-outlined">search_off</span>
              <p>{t("search.noResults", { query: searchText })}</p>
            </div>
          ) : (
            <>
              {results.tasks.length > 0 && (
                <div className="results-category">
                  <h3 className="category-title">
                    {t("search.tasksHeading", { count: results.tasks.length })}
                  </h3>
                  <div className="results-list">
                    {results.tasks.map((task, index) => (
                      <div
                        key={task.id}
                        ref={selectedIndex === index ? selectedItemRef : null}
                        className={`result-item task-item ${selectedIndex === index ? "selected" : ""}`}
                        onClick={() => handleTaskClick(task)}
                      >
                        <div className="result-header">
                          <span className="result-title">{task.title}</span>
                          {task.status && (
                            <span className={`status-badge ${task.status}`}>
                              {translateLabel(task.status, t)}
                            </span>
                          )}
                        </div>
                        {task.description && (
                          <p className="result-description">
                            {getTextPreview(task.description, 120)}
                          </p>
                        )}
                        <div className="result-meta">
                          <span
                            className="meta-item"
                            style={{
                              color: getColorHex(task.organization?.color),
                            }}
                          >
                            <span className="material-symbols-outlined">
                              business
                            </span>
                            {task.organization?.name || t("common.unknown")}
                          </span>
                          <span
                            className="meta-item"
                            style={{ color: getColorHex(task.project?.color) }}
                          >
                            <span className="material-symbols-outlined">
                              folder
                            </span>
                            {task.project?.name || t("common.unknown")}
                          </span>
                          {task.board && (
                            <span className="meta-item">
                              <span className="material-symbols-outlined">
                                {task.board.icon || "task_alt"}
                              </span>
                              {task.board.name}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {results.sections.length > 0 && (
                <div className="results-category">
                  <h3 className="category-title">
                    {t("search.sectionsHeading", {
                      count: results.sections.length,
                    })}
                  </h3>
                  <div className="results-list">
                    {results.sections.map((section, index) => {
                      const globalIndex = results.tasks.length + index;
                      return (
                        <div
                          key={section.id}
                          ref={
                            selectedIndex === globalIndex
                              ? selectedItemRef
                              : null
                          }
                          className={`result-item group-item ${selectedIndex === globalIndex ? "selected" : ""}`}
                          onClick={() => handleSectionClick(section)}
                        >
                          <div className="result-header">
                            <span
                              className="group-color-indicator"
                              style={{
                                backgroundColor: getColorHex(section.color),
                              }}
                            ></span>
                            <span className="result-title">{section.name}</span>
                            <span className="group-type-badge">
                              {t("search.section")}
                            </span>
                          </div>
                          <div className="result-meta">
                            <span
                              className="meta-item"
                              style={{
                                color: getColorHex(section.organization?.color),
                              }}
                            >
                              <span className="material-symbols-outlined">
                                business
                              </span>
                              {section.organization?.name ||
                                t("common.unknown")}
                            </span>
                            <span
                              className="meta-item"
                              style={{
                                color: getColorHex(section.project?.color),
                              }}
                            >
                              <span className="material-symbols-outlined">
                                folder
                              </span>
                              {section.project?.name || t("common.unknown")}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {results.notes.length > 0 && (
                <div className="results-category">
                  <h3 className="category-title">
                    {t("search.notesHeading", { count: results.notes.length })}
                  </h3>
                  <div className="results-list">
                    {results.notes.map((note, index) => {
                      const globalIndex =
                        results.tasks.length + results.sections.length + index;
                      return (
                        <div
                          key={note.id}
                          ref={
                            selectedIndex === globalIndex
                              ? selectedItemRef
                              : null
                          }
                          className={`result-item note-item ${selectedIndex === globalIndex ? "selected" : ""}`}
                          onClick={() => handleNoteClick(note)}
                        >
                          <div className="result-header">
                            <span className="result-title">{note.title}</span>
                          </div>
                          {note.content && (
                            <p className="result-description">
                              {getTextPreview(note.content, 120)}
                            </p>
                          )}
                          <div className="result-meta">
                            <span
                              className="meta-item"
                              style={{
                                color: getColorHex(note.organization?.color),
                              }}
                            >
                              <span className="material-symbols-outlined">
                                business
                              </span>
                              {note.organization?.name || t("common.unknown")}
                            </span>
                            <span
                              className="meta-item"
                              style={{
                                color: getColorHex(note.project?.color),
                              }}
                            >
                              <span className="material-symbols-outlined">
                                folder
                              </span>
                              {note.project?.name || t("common.unknown")}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {results.noteGroups.length > 0 && (
                <div className="results-category">
                  <h3 className="category-title">
                    {t("search.noteGroupsHeading", {
                      count: results.noteGroups.length,
                    })}
                  </h3>
                  <div className="results-list">
                    {results.noteGroups.map((noteGroup, index) => {
                      const globalIndex =
                        results.tasks.length +
                        results.sections.length +
                        results.notes.length +
                        index;
                      return (
                        <div
                          key={noteGroup.id}
                          ref={
                            selectedIndex === globalIndex
                              ? selectedItemRef
                              : null
                          }
                          className={`result-item group-item ${selectedIndex === globalIndex ? "selected" : ""}`}
                          onClick={() => handleNoteGroupClick(noteGroup)}
                        >
                          <div className="result-header">
                            <span
                              className="group-color-indicator"
                              style={{
                                backgroundColor: getColorHex(noteGroup.color),
                              }}
                            ></span>
                            <span className="result-title">
                              {noteGroup.name}
                            </span>
                            <span className="group-type-badge">
                              {t("search.noteGroup")}
                            </span>
                          </div>
                          <div className="result-meta">
                            <span
                              className="meta-item"
                              style={{
                                color: getColorHex(
                                  noteGroup.organization?.color,
                                ),
                              }}
                            >
                              <span className="material-symbols-outlined">
                                business
                              </span>
                              {noteGroup.organization?.name ||
                                t("common.unknown")}
                            </span>
                            <span
                              className="meta-item"
                              style={{
                                color: getColorHex(noteGroup.project?.color),
                              }}
                            >
                              <span className="material-symbols-outlined">
                                folder
                              </span>
                              {noteGroup.project?.name || t("common.unknown")}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {results.lists?.length > 0 && (
                <div className="results-category">
                  <h3 className="category-title">
                    {t("search.listsHeading", { count: results.lists.length })}
                  </h3>
                  <div className="results-list">
                    {results.lists.map((list, index) => {
                      const globalIndex =
                        results.tasks.length +
                        results.sections.length +
                        results.notes.length +
                        results.noteGroups.length +
                        index;
                      return (
                        <div
                          key={list.id}
                          ref={
                            selectedIndex === globalIndex
                              ? selectedItemRef
                              : null
                          }
                          className={`result-item list-item ${selectedIndex === globalIndex ? "selected" : ""}`}
                          onClick={() => handleListClick(list)}
                        >
                          <div className="result-header">
                            <span
                              className="list-color-indicator"
                              style={{
                                backgroundColor: list.color || "#2196f3",
                              }}
                            ></span>
                            <span className="result-title">{list.title}</span>
                            <span className="list-type-badge">
                              {list.scope === "personal"
                                ? t("search.personalList")
                                : t("search.projectList")}
                            </span>
                          </div>
                          <div className="result-description">
                            {t("search.itemsCompleted", {
                              completed: list.completedItems,
                              count: list.totalItems,
                            })}
                          </div>
                          <div className="result-meta">
                            {list.scope === "project" && list.organization && (
                              <span
                                className="meta-item"
                                style={{
                                  color: getColorHex(list.organization?.color),
                                }}
                              >
                                <span className="material-symbols-outlined">
                                  business
                                </span>
                                {list.organization?.name || t("common.unknown")}
                              </span>
                            )}
                            {list.scope === "project" && list.project && (
                              <span
                                className="meta-item"
                                style={{
                                  color: getColorHex(list.project?.color),
                                }}
                              >
                                <span className="material-symbols-outlined">
                                  folder
                                </span>
                                {list.project?.name || t("common.unknown")}
                              </span>
                            )}
                            {list.scope === "personal" && (
                              <span className="meta-item">
                                <span className="material-symbols-outlined">
                                  person
                                </span>
                                {t("search.personal")}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {results.boards?.length > 0 && (
                <div className="results-category">
                  <h3 className="category-title">
                    {t("search.boardsHeading", {
                      count: results.boards.length,
                    })}
                  </h3>
                  <div className="results-list">
                    {results.boards.map((board, index) => {
                      const globalIndex =
                        results.tasks.length +
                        results.sections.length +
                        results.notes.length +
                        results.noteGroups.length +
                        (results.lists?.length || 0) +
                        index;
                      return (
                        <div
                          key={board.id}
                          ref={
                            selectedIndex === globalIndex
                              ? selectedItemRef
                              : null
                          }
                          className={`result-item board-item ${selectedIndex === globalIndex ? "selected" : ""}`}
                          onClick={() => handleBoardClick(board)}
                        >
                          <div className="result-header">
                            {board.icon && (
                              <span className="material-symbols-outlined">
                                {board.icon}
                              </span>
                            )}
                            <span className="result-title">{board.name}</span>
                            <span className="board-type-badge">
                              {t("search.board")}
                            </span>
                          </div>
                          <div className="result-meta">
                            <span
                              className="meta-item"
                              style={{
                                color: getColorHex(board.organization?.color),
                              }}
                            >
                              <span className="material-symbols-outlined">
                                business
                              </span>
                              {board.organization?.name || t("common.unknown")}
                            </span>
                            <span
                              className="meta-item"
                              style={{
                                color: getColorHex(board.project?.color),
                              }}
                            >
                              <span className="material-symbols-outlined">
                                folder
                              </span>
                              {board.project?.name || t("common.unknown")}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SuperSearchModal;
